import { useState, useEffect } from 'react';
import { payoutAPI } from '../lib/api';
import { 
  DollarSign, TrendingUp, Car, Package, Star, Calendar,
  ChevronDown, Loader2, MapPin, Clock, Download, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { devError } from '../lib/devLog';

const PayoutReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await payoutAPI.getReport(period);
      setReport(response.data);
    } catch (error) {
      devError('Failed to fetch payout report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await payoutAPI.exportReport(format, period);
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payout_${period}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12 text-zinc-500">
        Failed to load payout report
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with period selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Payout Report
          </h2>
          <p className="text-sm text-zinc-500">{report.period}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px]" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Today</SelectItem>
            <SelectItem value="week">Last 7 Days</SelectItem>
            <SelectItem value="month">Last 30 Days</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <DollarSign className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-2xl font-bold">${report.summary.total_earnings.toFixed(2)}</p>
          <p className="text-sm opacity-80">Total Earnings</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <Car className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-2xl font-bold">{report.summary.total_jobs}</p>
          <p className="text-sm opacity-80">Jobs Completed</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <TrendingUp className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-2xl font-bold">${report.summary.average_per_job.toFixed(2)}</p>
          <p className="text-sm opacity-80">Avg per Job</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl p-4 text-white">
          <Star className="w-6 h-6 mb-2 opacity-80" />
          <p className="text-2xl font-bold">{report.summary.driver_rating.toFixed(1)}</p>
          <p className="text-sm opacity-80">{report.summary.total_ratings} ratings</p>
        </div>
      </div>

      {/* Breakdown by Type */}
      <div className="bg-white border border-zinc-200 rounded-xl p-5">
        <h3 className="font-semibold text-zinc-900 mb-4">Earnings by Type</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-zinc-900">${report.by_type.rides.earnings.toFixed(2)}</p>
              <p className="text-xs text-zinc-500">{report.by_type.rides.count} rides</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-zinc-900">${report.by_type.deliveries.earnings.toFixed(2)}</p>
              <p className="text-xs text-zinc-500">{report.by_type.deliveries.count} deliveries</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Breakdown */}
      {report.by_day.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-zinc-900 mb-4">Daily Breakdown</h3>
          <div className="space-y-2">
            {report.by_day.map((day, i) => (
              <div key={day.date || i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-600">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-zinc-500">{day.jobs} jobs</span>
                  <span className="font-semibold text-green-600">${day.earnings.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zone Breakdown */}
      {report.by_zone.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-zinc-900 mb-4">Earnings by Zone</h3>
          <div className="space-y-2">
            {report.by_zone.map((zone, i) => (
              <div key={zone.zone || i} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-600">{zone.zone}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-zinc-500">{zone.jobs} jobs</span>
                  <span className="font-semibold text-zinc-900">${zone.earnings.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Jobs */}
      {report.recent_jobs.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <h3 className="font-semibold text-zinc-900 mb-4">Recent Jobs</h3>
          <div className="space-y-3">
            {report.recent_jobs.map((job, i) => (
              <div key={job.id || i} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-lg">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  job.type === 'ride' ? 'bg-blue-100' : 'bg-orange-100'
                }`}>
                  {job.type === 'ride' 
                    ? <Car className={`w-4 h-4 text-blue-600`} />
                    : <Package className={`w-4 h-4 text-orange-600`} />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-900 capitalize">
                      {job.type === 'ride' ? 'Ride' : `${job.category || 'Package'} Delivery`}
                    </span>
                    <span className="font-semibold text-green-600">${job.price.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-zinc-500 truncate mt-0.5">
                    {job.pickup} → {job.dropoff}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
                    <Clock className="w-3 h-3" />
                    {new Date(job.completed_at).toLocaleString()}
                    <span className="bg-zinc-200 px-1.5 py-0.5 rounded text-zinc-600">{job.zone}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {report.recent_jobs.length === 0 && (
        <div className="text-center py-8 text-zinc-500">
          <Car className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
          <p>No completed jobs in this period</p>
        </div>
      )}
    </div>
  );
};

export default PayoutReport;
